import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import VerifiedIcon from '@mui/icons-material/Verified';
import type { ConsolidatedEventCluster } from '@/types';
import { formatVideoTime, getJudgementTextColor } from '@/types';
import JudgementOutcomeLine from '@/components/review/JudgementOutcomeLine';
import {
  agreementMetricColor,
  consensusDistributions,
  formatClusterAgreementDisplayLabel,
  formatClusterOnFieldDecision,
  getClusterOwnerAssertion,
  isParticipantAlignedWithOwner,
} from '@/components/review/consensus';

type DistributionRow = { label: string; count: number };

const BAR_FILL = 'rgba(56, 142, 60, 0.35)';

function judgementLabelColor(label: string): string | undefined {
  if (label === '正確') return getJudgementTextColor('correct');
  if (label === '錯誤') return getJudgementTextColor('wrong');
  if (label === '灰色') return getJudgementTextColor('uncertain');
  return undefined;
}

function buildSummaryLine(
  distributions: ReturnType<typeof consensusDistributions>,
  onFieldTitle: string
): string {
  const ballTop = distributions.reviewedBall[0];
  const ballRunner = distributions.reviewedBall[1];
  const sanctionTop = distributions.reviewedSanction[0];
  if (ballTop && ballRunner) return `覆核球：${ballTop.label} / ${ballRunner.label}`;
  if (ballTop) return `覆核球：${ballTop.label}`;
  if (sanctionTop) return `覆核罰：${sanctionTop.label}`;
  if (distributions.judgement.length > 1) {
    return `評價：${distributions.judgement.map((j) => j.label).join(' / ')}`;
  }
  return onFieldTitle;
}

function DistributionMiniPanel({
  title,
  rows,
  total,
  keyPrefix,
  renderLabel,
}: {
  title: string;
  rows: DistributionRow[];
  total: number;
  keyPrefix: string;
  renderLabel?: (label: string) => React.ReactNode;
}) {
  return (
    <Box
      sx={{
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: (t) => alpha(t.palette.background.default, 0.6),
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
        {title}
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.disabled">
          —
        </Typography>
      ) : (
        <Stack spacing={0.85}>
          {rows.map((row) => {
            const pct = Math.round((row.count / Math.max(1, total)) * 100);
            const color = judgementLabelColor(row.label);
            return (
              <Box key={`${keyPrefix}-${row.label}`}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={0.5}>
                  {renderLabel ? (
                    renderLabel(row.label)
                  ) : color ? (
                    <Typography variant="body2" fontWeight={700} sx={{ color, lineHeight: 1.3 }}>
                      {row.label}
                    </Typography>
                  ) : (
                    <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                      {row.label}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                    {row.count} · {pct}%
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    mt: 0.35,
                    height: 5,
                    borderRadius: 4,
                    bgcolor: 'action.hover',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: BAR_FILL, minWidth: pct > 0 ? 4 : 0 }} />
                </Box>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
}

interface Props {
  cluster: ConsolidatedEventCluster;
  officialUserIds?: number[];
  onJumpToTime?: (seconds: number) => void;
  onPlayVideo?: (cluster: ConsolidatedEventCluster) => void;
  participantView?: boolean;
  currentUserId?: number;
}

export default function DiscrepancyEventCard({
  cluster,
  officialUserIds,
  onJumpToTime,
  onPlayVideo,
  participantView = false,
  currentUserId,
}: Props) {
  const theme = useTheme();
  const distributions = consensusDistributions(cluster);
  const ownerAssertion = getClusterOwnerAssertion(cluster, officialUserIds);
  const onFieldTitle = formatClusterOnFieldDecision(cluster);
  const summaryLine = buildSummaryLine(distributions, onFieldTitle);
  const participantCount = cluster.assertions.filter((a) => a.role === 'participant').length;
  const total = Math.max(1, cluster.assertions.length);
  const rate = cluster.agreementRate;
  const rowTone = agreementMetricColor(rate ?? 0, theme.palette.mode === 'dark');

  const myAssertion =
    participantView && currentUserId != null
      ? cluster.assertions.find(
          (a) => a.role === 'participant' && a.userId === String(currentUserId)
        ) ?? null
      : null;
  const myAligned =
    participantView && currentUserId != null
      ? isParticipantAlignedWithOwner(cluster, currentUserId, officialUserIds)
      : null;

  const playSeconds = Math.max(0, cluster.anchorTimestamp - 0.8);

  return (
    <Accordion
      disableGutters
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        '&:before': { display: 'none' },
        flexShrink: 0,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          minHeight: 48,
          px: 1.25,
          '& .MuiAccordionSummary-content': { my: 0.75, alignItems: 'center' },
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 0.5, minWidth: 0 }}>
          <Typography
            variant="body2"
            fontWeight={700}
            fontFamily="monospace"
            sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}
          >
            {formatVideoTime(cluster.anchorTimestamp)}
          </Typography>
          {(onPlayVideo || onJumpToTime) && (
            <Tooltip title={onPlayVideo ? '播放影片片段' : '跳到影片'}>
              <IconButton
                size="small"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onPlayVideo) onPlayVideo(cluster);
                  else onJumpToTime?.(playSeconds);
                }}
                sx={{ p: 0.25, flexShrink: 0 }}
              >
                <PlayArrowRoundedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1, minWidth: 0 }} noWrap title={summaryLine}>
            {summaryLine}
          </Typography>
          <Chip
            size="small"
            label={
              participantView
                ? myAligned == null
                  ? '未標記'
                  : myAligned
                    ? '一致'
                    : '分歧'
                : formatClusterAgreementDisplayLabel(cluster, officialUserIds)
            }
            sx={{
              height: 20,
              fontSize: '0.68rem',
              fontWeight: 700,
              flexShrink: 0,
              bgcolor: rowTone.bg,
              color: rowTone.color,
              border: '1px solid',
              borderColor: rowTone.border,
            }}
          />
        </Stack>
      </AccordionSummary>

      <AccordionDetails sx={{ px: 1.25, pt: 0, pb: 1.25 }}>
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              場上判決 · {onFieldTitle}
            </Typography>
            <Chip
              size="small"
              icon={<PeopleOutlineIcon sx={{ fontSize: '14px !important' }} />}
              label={`${participantCount} 位參與`}
              variant="outlined"
              sx={{ height: 20 }}
            />
            <Typography variant="caption" color="text.secondary">
              Δt {(cluster.averageVariance || 0).toFixed(2)} 秒
            </Typography>
          </Stack>

          {participantView && myAssertion ? (
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.25}
              sx={{
                p: 1.25,
                borderRadius: 1.5,
                bgcolor: (t) => alpha(t.palette.warning.main, 0.08),
                border: '1px solid',
                borderColor: (t) => alpha(t.palette.warning.main, 0.25),
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  我的判決
                </Typography>
                <JudgementOutcomeLine
                  variant="body2"
                  judgement={myAssertion.judgement}
                  reviewedDecision={myAssertion.reviewedBall}
                  reviewedPunishment={myAssertion.reviewedSanction}
                  sx={{ color: 'text.primary', fontSize: '0.9rem', mt: 0.25 }}
                />
              </Box>
              {ownerAssertion && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.25 }}>
                    <VerifiedIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
                    <Typography variant="caption" color="text.secondary" fontWeight={700}>
                      官方
                    </Typography>
                  </Stack>
                  <JudgementOutcomeLine
                    variant="body2"
                    judgement={ownerAssertion.judgement}
                    reviewedDecision={ownerAssertion.reviewedBall}
                    reviewedPunishment={ownerAssertion.reviewedSanction}
                    sx={{ color: 'text.primary', fontSize: '0.9rem' }}
                  />
                </Box>
              )}
            </Stack>
          ) : (
            ownerAssertion && (
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: (t) => alpha(t.palette.secondary.main, 0.06),
                  border: '1px solid',
                  borderColor: (t) => alpha(t.palette.secondary.main, 0.2),
                }}
              >
                <VerifiedIcon sx={{ fontSize: 16, color: 'secondary.main', mt: 0.2, flexShrink: 0 }} />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={700}>
                    官方覆核
                  </Typography>
                  <JudgementOutcomeLine
                    variant="body2"
                    judgement={ownerAssertion.judgement}
                    reviewedDecision={ownerAssertion.reviewedBall}
                    reviewedPunishment={ownerAssertion.reviewedSanction}
                    sx={{ color: 'text.primary', fontSize: '0.9rem', mt: 0.25 }}
                  />
                </Box>
              </Stack>
            )
          )}

          <Divider />

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
              gap: 1,
            }}
          >
            <DistributionMiniPanel
              title="覆核判決評價"
              rows={distributions.judgement}
              total={total}
              keyPrefix="judgement"
              renderLabel={(label) => (
                <Typography variant="body2" fontWeight={700} sx={{ color: judgementLabelColor(label) }}>
                  {label}
                </Typography>
              )}
            />
            <DistributionMiniPanel
              title="覆核球權"
              rows={distributions.reviewedBall}
              total={total}
              keyPrefix="ball"
            />
            <DistributionMiniPanel
              title="覆核罰則"
              rows={distributions.reviewedSanction}
              total={total}
              keyPrefix="sanction"
            />
          </Box>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
